import { TextOverlay } from "../types/overlayTypes";

/**
 * Calculate text lines with word wrapping
 * Handles newlines first, then word-wraps each paragraph
 */
export const calculateTextLines = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string[] => {
  const paragraphs = text.split("\n");
  const lines: string[] = [];

  for (const paragraph of paragraphs) {
    const words = paragraph.split(" ");
    let currentLine = "";

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const testWidth = ctx.measureText(testLine).width;

      if (testWidth > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }

    if (currentLine) {
      lines.push(currentLine);
    }
  }

  return lines;
};

/**
 * Calculate text dimensions (width, height, padded dimensions)
 */
export interface TextDimensions {
  textWidth: number;
  textHeight: number;
  paddedWidth: number;
  paddedHeight: number;
  lines: string[];
  lineHeight: number;
}

export const calculateTextDimensions = (
  ctx: CanvasRenderingContext2D,
  text: string,
  fontSize: number,
  padding: number,
  maxWidth?: number
): TextDimensions => {
  const lineHeight = fontSize * 1.2;
  let lines: string[] = [];
  let textWidth: number;
  let textHeight: number;

  if (maxWidth) {
    lines = calculateTextLines(ctx, text, maxWidth);
    textHeight = lines.length * lineHeight;
    textWidth = Math.max(...lines.map((line) => ctx.measureText(line).width));
  } else {
    // When no maxWidth, just split by newlines
    lines = text.split("\n");
    textHeight = lines.length * lineHeight;
    textWidth = Math.max(...lines.map((line) => ctx.measureText(line).width));
  }

  const paddedWidth = textWidth + padding * 2;
  const paddedHeight = textHeight + padding * 2;

  return {
    textWidth,
    textHeight,
    paddedWidth,
    paddedHeight,
    lines,
    lineHeight,
  };
};

/**
 * Create a temporary canvas context with the same font settings as the overlay
 */
export const createTextContext = (
  overlay: TextOverlay,
  fontSize?: number
): CanvasRenderingContext2D | null => {
  const tempCanvas = document.createElement("canvas");
  const tempCtx = tempCanvas.getContext("2d");
  if (!tempCtx) {
    return null;
  }

  const fontStyle = overlay.isItalic ? "italic " : "";
  const fontWeight = overlay.isBold ? "bold" : "normal";
  const fontFamily = overlay.fontFamily || "Arial";
  const size = fontSize || overlay.fontSize;
  tempCtx.font = `${fontStyle}${fontWeight} ${size}px ${fontFamily}`;

  return tempCtx;
};

/**
 * Calculate rotated corner positions
 */
export const calculateRotatedCorners = (
  centerX: number,
  centerY: number,
  width: number,
  height: number,
  rotation = 0
): Array<{ x: number; y: number }> => {
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  const unrotatedCorners = [
    { x: -halfWidth, y: -halfHeight },
    { x: halfWidth, y: -halfHeight },
    { x: -halfWidth, y: halfHeight },
    { x: halfWidth, y: halfHeight },
  ];

  if (!rotation || rotation === 0) {
    return unrotatedCorners.map(({ x, y }) => ({
      x: centerX + x,
      y: centerY + y,
    }));
  }

  const rotationRad = (rotation * Math.PI) / 180;
  const cosRot = Math.cos(rotationRad);
  const sinRot = Math.sin(rotationRad);

  return unrotatedCorners.map(({ x: relX, y: relY }) => {
    const rotatedX = relX * cosRot - relY * sinRot;
    const rotatedY = relX * sinRot + relY * cosRot;
    return {
      x: centerX + rotatedX,
      y: centerY + rotatedY,
    };
  });
};

/**
 * Check if a point is inside a rotated rectangle
 * Uses the point-in-polygon algorithm for the rotated rectangle
 */
export const isPointInRotatedRectangle = (
  pointX: number,
  pointY: number,
  centerX: number,
  centerY: number,
  width: number,
  height: number,
  rotation = 0
): boolean => {
  // If no rotation, use simple rectangle check
  if (!rotation || rotation === 0) {
    const halfWidth = width / 2;
    const halfHeight = height / 2;
    return (
      pointX >= centerX - halfWidth &&
      pointX <= centerX + halfWidth &&
      pointY >= centerY - halfHeight &&
      pointY <= centerY + halfHeight
    );
  }

  // For rotated rectangles, use point-in-polygon algorithm
  // First, transform the point to the rectangle's local coordinate system
  const rotationRad = (-rotation * Math.PI) / 180; // Negative to reverse the rotation
  const cosRot = Math.cos(rotationRad);
  const sinRot = Math.sin(rotationRad);

  // Translate point relative to center
  const dx = pointX - centerX;
  const dy = pointY - centerY;

  // Rotate point back to local coordinates
  const localX = dx * cosRot - dy * sinRot;
  const localY = dx * sinRot + dy * cosRot;

  // Check if point is inside unrotated rectangle
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  return (
    localX >= -halfWidth &&
    localX <= halfWidth &&
    localY >= -halfHeight &&
    localY <= halfHeight
  );
};
